import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { print } from 'graphql'
import { createClient } from 'graphql-ws'

const wsUrl =
  import.meta.env.VITE_GRAPHQL_WS_URL ??
  `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/graphql`

/**
 * The subscription transport, on the same path as queries and mutations.
 *
 * The client is lazy, so until something calls `subscribe` no socket is opened,
 * and all of an app's subscriptions share the one connection.
 *
 * Authentication rides the cookie on the upgrade request; there are no tokens
 * in connectionParams.
 */
export const wsClient = createClient({
  url: wsUrl,
  lazy: true,
  keepAlive: 10_000,
  retryAttempts: Infinity,
})

/** What `subscribe` reports back. Every handler is optional. */
interface Handlers<TResult> {
  onNext: (data: TResult) => void
  /**
   * Called when the stream starts, and again after any restart.
   *
   * This is where a feature refetches. A subscription can miss events in two
   * ways — the socket dropped, or the server closed a stream that had fallen
   * behind — and in both cases the cache is stale by an unknown amount. The
   * server deliberately *ends* a lagging stream rather than skipping ahead, so
   * that this callback is the recovery point.
   */
  onStart?: () => void
}

/** How long to wait before rebuilding a stream the server completed. */
const RESTART_DELAY = 1_000

/**
 * Subscribe with a generated document, fully typed, and resubscribe on its own.
 *
 * graphql-ws wants a query *string*, while codegen hands us a
 * `TypedDocumentNode`; `print` is the bridge, and doing it here means no
 * feature has to think about it.
 *
 * The returned function unsubscribes. Nothing else in the app talks to
 * `wsClient` directly.
 */
export function subscribe<TResult, TVariables extends Record<string, unknown>>(
  document: TypedDocumentNode<TResult, TVariables>,
  variables: TVariables,
  { onNext, onStart }: Handlers<TResult>,
): () => void {
  const query = print(document)
  let disposed = false
  let cancel: (() => void) | undefined
  let restartTimer: ReturnType<typeof setTimeout> | undefined

  const start = () => {
    if (disposed) return
    onStart?.()

    cancel = wsClient.subscribe<TResult>(
      { query, variables },
      {
        next: ({ data }) => {
          if (data) onNext(data)
        },
        // graphql-ws retries the socket itself, so an error here is the
        // subscription failing rather than the connection — most often being
        // kicked from the lounge mid-stream. Retrying that would be a loop
        // against a server that has already said no.
        error: (error) => {
          console.warn('subscription failed', error)
        },
        // A completed subscription is not normal for a stream that should run
        // until unsubscribed: the server ends one when membership is revoked,
        // or when this client has fallen too far behind to be sure it saw
        // everything. Retrying covers the second case (and costs one refused
        // subscribe in the first), and `onStart` refetches what was missed.
        complete: () => {
          if (disposed) return
          restartTimer = setTimeout(start, RESTART_DELAY)
        },
      },
    )
  }

  start()

  return () => {
    disposed = true
    if (restartTimer) clearTimeout(restartTimer)
    cancel?.()
  }
}
